import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import DateRangePicker from "../components/ui/DateRangePicker";
import PhotoCarouselModal from "../components/PhotoCarouselModal";
import Button from "../components/ui/Button";
import { useT } from "../contexts/TranslationContext";
import { getCloudinaryThumbUrl } from "../utils/cloudinaryMedia";

const SESSION_KEY_PREFIX = "projectClientShareSession:";
const PHOTO_PAGE_SIZE = 32;
const PERIOD_IDS = ["today", "last14", "last30"];

function toDateKey(d) {
  if (!d) return null;
  return dayjs(d).format("YYYY-MM-DD");
}

function rangesMatch(a, b) {
  if (!a?.from || !a?.to || !b?.from || !b?.to) return false;
  return toDateKey(a.from) === toDateKey(b.from) && toDateKey(a.to) === toDateKey(b.to);
}

function periodRange(id) {
  const to = dayjs().endOf("day").toDate();
  if (id === "today") {
    return { from: dayjs().startOf("day").toDate(), to };
  }
  if (id === "last14") {
    return { from: dayjs().subtract(13, "day").startOf("day").toDate(), to };
  }
  if (id === "last30") {
    return { from: dayjs().subtract(29, "day").startOf("day").toDate(), to };
  }
  return null;
}

function matchingPeriodId(range) {
  if (!range?.from || !range?.to) return null;
  for (const id of PERIOD_IDS) {
    if (rangesMatch(range, periodRange(id))) return id;
  }
  return "custom";
}

function sessionKey(token) {
  return `${SESSION_KEY_PREFIX}${token}`;
}

function getStoredSession(token) {
  try {
    return sessionStorage.getItem(sessionKey(token)) || "";
  } catch {
    return "";
  }
}

function setStoredSession(token, sessionToken) {
  try {
    if (sessionToken) sessionStorage.setItem(sessionKey(token), sessionToken);
    else sessionStorage.removeItem(sessionKey(token));
  } catch {
    /* ignore */
  }
}

/**
 * Public (no login) project progress portal for external clients.
 */
export default function ProjectClientPortal() {
  const { token } = useParams();
  const { t } = useT();

  const [meta, setMeta] = useState(null);
  const [sessionToken, setSessionToken] = useState(() => getStoredSession(token));
  const [pin, setPin] = useState("");
  const [project, setProject] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState("");
  const [reports, setReports] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [photosPage, setPhotosPage] = useState(1);
  const [photosHasMore, setPhotosHasMore] = useState(false);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [photoModal, setPhotoModal] = useState({ isOpen: false, items: [], index: 0 });
  const customPeriodRef = useRef(null);
  const previousPeriodIdRef = useRef(null);

  const defaultRange = useMemo(() => periodRange("last14"), []);

  const [range, setRange] = useState(defaultRange);
  const matchedPeriodId = matchingPeriodId(range);
  const periodId = matchedPeriodId ?? previousPeriodIdRef.current ?? "last14";

  const authHeaders = useMemo(() => {
    if (!sessionToken) return {};
    return { Authorization: `Bearer ${sessionToken}` };
  }, [sessionToken]);

  const loadMeta = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`/api/public/project-share/${token}`, {
        headers: authHeaders,
      });
      setMeta(res.data);
      if (res.data.unlocked && res.data.project) {
        setProject(res.data.project);
        setUnlocked(true);
      } else if (!res.data.requiresPin) {
        // No PIN: unlock to obtain a session for consistency / lastAccessed tracking
        const unlockRes = await axios.post(`/api/public/project-share/${token}/unlock`, {});
        setStoredSession(token, unlockRes.data.sessionToken);
        setSessionToken(unlockRes.data.sessionToken);
        setProject(unlockRes.data.project);
        setUnlocked(true);
        setMeta((m) => ({ ...m, unlocked: true, requiresPin: false }));
      } else {
        setUnlocked(false);
        setProject(null);
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.status === 404
          ? t("projectClientShare.notFound")
          : t("projectClientShare.loadError")
      );
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders, t]);

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when token changes
  }, [token]);

  const handleUnlock = async (e) => {
    e?.preventDefault();
    setUnlocking(true);
    setError("");
    try {
      const res = await axios.post(`/api/public/project-share/${token}/unlock`, {
        pin: pin.trim(),
      });
      setStoredSession(token, res.data.sessionToken);
      setSessionToken(res.data.sessionToken);
      setProject(res.data.project);
      setUnlocked(true);
      setPin("");
    } catch (err) {
      console.error(err);
      setError(
        err.response?.status === 401
          ? t("projectClientShare.incorrectPin")
          : t("projectClientShare.unlockError")
      );
    } finally {
      setUnlocking(false);
    }
  };

  const rangeParams = useMemo(() => {
    if (!range?.from || !range?.to) return null;
    return {
      startDate: dayjs(range.from).format("YYYY-MM-DD"),
      endDate: dayjs(range.to).format("YYYY-MM-DD"),
    };
  }, [range]);

  const loadReports = useCallback(async () => {
    if (!unlocked || !rangeParams || !token) return;
    setReportsLoading(true);
    setError("");
    try {
      const headers = {
        Authorization: `Bearer ${getStoredSession(token) || sessionToken}`,
      };
      const reportsRes = await axios.get(`/api/public/project-share/${token}/reports`, {
        params: rangeParams,
        headers,
      });
      setReports(reportsRes.data.reports || []);
      setTotalHours(reportsRes.data.totalHours || 0);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        setUnlocked(false);
        setStoredSession(token, "");
        setSessionToken("");
        setError(t("projectClientShare.sessionExpired"));
      } else {
        setError(t("projectClientShare.loadReportsError"));
      }
    } finally {
      setReportsLoading(false);
    }
  }, [unlocked, rangeParams, token, sessionToken, t]);

  const loadPhotosPage = useCallback(
    async (page, append) => {
      if (!unlocked || !rangeParams || !token) return;
      setPhotosLoading(true);
      try {
        const headers = {
          Authorization: `Bearer ${getStoredSession(token) || sessionToken}`,
        };
        const photosRes = await axios.get(`/api/public/project-share/${token}/photos`, {
          params: { ...rangeParams, page, limit: PHOTO_PAGE_SIZE },
          headers,
        });
        const items = photosRes.data.items || [];
        setPhotos((prev) => (append ? [...prev, ...items] : items));
        setPhotosPage(page);
        setPhotosHasMore(!!photosRes.data.hasMore);
      } catch (err) {
        console.error(err);
        if (err.response?.status === 401) {
          setUnlocked(false);
          setStoredSession(token, "");
          setSessionToken("");
          setError(t("projectClientShare.sessionExpired"));
        } else if (!append) {
          setPhotos([]);
          setPhotosHasMore(false);
        }
      } finally {
        setPhotosLoading(false);
      }
    },
    [unlocked, rangeParams, token, sessionToken, t]
  );

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    if (!rangeParams) return;
    setPhotos([]);
    setPhotosPage(1);
    setPhotosHasMore(false);
    loadPhotosPage(1, false);
  }, [loadPhotosPage, rangeParams]);

  useEffect(() => {
    if (periodId === "custom" && previousPeriodIdRef.current !== "custom") {
      customPeriodRef.current?.focus();
    }
    previousPeriodIdRef.current = periodId;
  }, [periodId]);

  const handleExport = async () => {
    if (!rangeParams) return;
    setExporting(true);
    try {
      const headers = {
        Authorization: `Bearer ${getStoredSession(token) || sessionToken}`,
      };
      const res = await axios.get(`/api/public/project-share/${token}/export.pdf`, {
        params: rangeParams,
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(project?.name || "project").replace(/\s+/g, "_")}_${rangeParams.startDate}_${rangeParams.endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError(t("projectClientShare.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const openPhotos = (urls, report) => {
    if (!urls?.length) return;
    setPhotoModal({
      isOpen: true,
      items: urls.map((url) => ({
        url,
        date: report?.date,
        description: report?.description || "",
        reportId: report?._id,
      })),
      index: 0,
    });
  };

  const selectPeriod = (id) => {
    const next = periodRange(id);
    if (next) setRange(next);
  };

  const loadMorePhotos = () => {
    if (photosLoading || !photosHasMore) return;
    loadPhotosPage(photosPage + 1, true);
  };

  const periodPills = [
    { id: "today", label: t("projectClientShare.presetToday") },
    { id: "last14", label: t("projectClientShare.presetLast14") },
    { id: "last30", label: t("projectClientShare.presetDays30") },
  ];

  const pillClass = (active) =>
    `rounded-full px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1 ${
      active
        ? "bg-slate-900 text-white"
        : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
    }`;

  const addressLine = [project?.address, project?.zipCode, project?.city]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-stone-50">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <header className="mb-8">
          {project ? (
            <>
              <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
                {project.name}
              </h1>
              {addressLine && (
                <p className="mt-2 text-slate-600">{addressLine}</p>
              )}
            </>
          ) : (
            <h1 className="text-3xl font-semibold text-slate-900">
              {t("projectClientShare.portalTitle")}
            </h1>
          )}
        </header>

        {loading && (
          <p className="text-slate-500">{t("projectDetails.loading")}</p>
        )}

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && meta?.requiresPin && !unlocked && (
          <form
            onSubmit={handleUnlock}
            className="rounded-lg border border-slate-200 bg-white/80 backdrop-blur px-5 py-6 shadow-sm max-w-sm"
          >
            <h2 className="text-lg font-medium text-slate-900 mb-1">
              {t("projectClientShare.enterPin")}
            </h2>
            <p className="text-sm text-slate-600 mb-4">
              {t("projectClientShare.enterPinHint")}
            </p>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-lg tracking-widest mb-4"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              placeholder="••••"
              autoFocus
            />
            <Button type="submit" disabled={unlocking || pin.length < 4}>
              {unlocking ? t("projectDetails.loading") : t("projectClientShare.unlock")}
            </Button>
          </form>
        )}

        {unlocked && project && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 justify-between rounded-lg border border-slate-200 bg-white/80 px-4 py-4 shadow-sm">
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">
                  {t("projectClientShare.period")}
                </p>
                <div
                  role="radiogroup"
                  aria-label={t("projectClientShare.period")}
                  className="flex flex-wrap gap-1.5 mb-3"
                >
                  {periodPills.map((pill) => (
                    <button
                      key={pill.id}
                      type="button"
                      role="radio"
                      aria-checked={periodId === pill.id}
                      className={pillClass(periodId === pill.id)}
                      onClick={() => selectPeriod(pill.id)}
                    >
                      {pill.label}
                    </button>
                  ))}
                  {periodId === "custom" && (
                    <button
                      ref={customPeriodRef}
                      type="button"
                      role="radio"
                      aria-checked="true"
                      className={pillClass(true)}
                    >
                      {t("projectClientShare.presetCustom")}
                    </button>
                  )}
                </div>
                <DateRangePicker
                  value={range}
                  onChange={setRange}
                  placeholder={t("projectClientShare.selectPeriod")}
                />
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <p className="text-sm text-slate-600 mr-2">
                  {t("projectClientShare.totalHours", {
                    hours: Number.isFinite(totalHours)
                      ? Math.round(totalHours * 100) / 100
                      : totalHours,
                  })}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleExport}
                  disabled={exporting || !rangeParams}
                >
                  {exporting
                    ? t("projectDetails.working")
                    : t("projectClientShare.exportPdf")}
                </Button>
              </div>
            </div>

            {reportsLoading && (
              <p className="text-slate-500 text-sm">{t("projectClientShare.loadingReports")}</p>
            )}

            {!reportsLoading && reports.length === 0 && (
              <p className="text-slate-600 text-sm py-6 text-center">
                {t("projectClientShare.noReportsInPeriod")}
              </p>
            )}

            <div className="space-y-3">
              {reports.map((report) => {
                const photoCount = report.photos?.length || 0;
                const hasTasks = report.taskEntries?.length > 0;
                return (
                  <article
                    key={report._id}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-4 shadow-sm"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                      <time className="text-sm font-semibold text-slate-900">
                        {dayjs(report.date).format("D MMM YYYY")}
                      </time>
                      <span className="text-sm font-semibold text-slate-700">
                        {report.hours}
                        {t("timeReports.hourSuffix")}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">
                      {report.worker?.name || t("projectDetails.unknownWorker")}
                    </p>

                    {report.description?.trim() && (
                      <p className="text-sm text-slate-800 mb-2 whitespace-pre-wrap">
                        {report.description}
                      </p>
                    )}

                    {hasTasks && (
                      <ul className="space-y-2 mb-2">
                        {report.taskEntries.map((entry, idx) => (
                          <li key={idx} className="text-sm">
                            <div className="flex justify-between gap-2 font-medium text-slate-900">
                              <span>{entry.title || t("projectDetails.taskN", { number: idx + 1 })}</span>
                              <span className="text-slate-600 flex-shrink-0">
                                {entry.hours}
                                {t("timeReports.hourSuffix")}
                              </span>
                            </div>
                            {entry.description?.trim() && (
                              <p className="text-slate-600 text-xs mt-0.5 pl-2 border-l-2 border-slate-200">
                                {entry.description}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {photoCount > 0 && (
                      <button
                        type="button"
                        className="mt-2 flex gap-2 items-center"
                        onClick={() => openPhotos(report.photos, report)}
                      >
                        {report.photos.slice(0, 3).map((url, i) => (
                          <img
                            key={i}
                            src={getCloudinaryThumbUrl(url)}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="h-14 w-14 object-cover rounded border border-slate-200"
                          />
                        ))}
                        <span className="text-xs text-slate-600">
                          {photoCount}{" "}
                          {photoCount === 1
                            ? t("projectDetails.photo")
                            : t("projectDetails.photos")}
                        </span>
                      </button>
                    )}
                  </article>
                );
              })}
            </div>

            {(photos.length > 0 || photosLoading) && (
              <section className="pt-4">
                <h2 className="text-lg font-semibold text-slate-900 mb-3">
                  {t("projectDetails.photosSection")}
                </h2>
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {photos.map((p, idx) => (
                      <button
                        key={`${p.reportId}-${idx}`}
                        type="button"
                        className="aspect-square overflow-hidden rounded border border-slate-200"
                        onClick={() =>
                          setPhotoModal({
                            isOpen: true,
                            items: photos,
                            index: idx,
                          })
                        }
                      >
                        <img
                          src={getCloudinaryThumbUrl(p.url)}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
                {photosLoading && photos.length === 0 && (
                  <p className="text-slate-500 text-sm">{t("projectDetails.loadingPhotos")}</p>
                )}
                {photosHasMore && (
                  <div className="mt-4 text-center">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={loadMorePhotos}
                      disabled={photosLoading}
                    >
                      {photosLoading
                        ? t("projectDetails.loadingPhotos")
                        : t("projectDetails.loadMore")}
                    </Button>
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </div>

      <PhotoCarouselModal
        isOpen={photoModal.isOpen}
        items={photoModal.items}
        initialIndex={photoModal.index}
        onClose={() => setPhotoModal({ isOpen: false, items: [], index: 0 })}
      />
    </div>
  );
}
